import { useEffect, useMemo, useState } from "react";
import { ChartCard, Skeleton, TierBadge } from "@/components/reusable";
import { fetchBeforeAfterFiles } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { FileBeforeAfter, FileColumnMappingDetail } from "@/types/analytics";

function tierForBadge(t: FileColumnMappingDetail["tier"]) {
  if (t === "manual") return "Manual" as const;
  return t;
}

function pct(part: number, total: number): string {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

/**
 * Single-file drill-in showing raw supplier headers → resolved mappings,
 * with tier attribution. Replaces a "time saved" claim with a concrete
 * before/after that the client can verify against the file itself.
 */
export function BeforeAfterDrilldown() {
  const filters = useAnalyticsFilters();
  const [files, setFiles] = useState<FileBeforeAfter[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setFiles(null);
    fetchBeforeAfterFiles(filters).then((f) => {
      if (!active) return;
      setFiles(f);
      setSelectedId((prev) =>
        prev && f.some((x) => x.fileId === prev) ? prev : (f[0]?.fileId ?? null)
      );
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const selected = useMemo(
    () => files?.find((f) => f.fileId === selectedId) ?? null,
    [files, selectedId]
  );

  return (
    <ChartCard
      title="File before & after"
      subtitle="What each file looked like raw, versus what klyra resolved"
      height={500}
      headerRight={
        files && files.length > 0 ? (
          <select
            value={selectedId ?? ""}
            onChange={(e) => setSelectedId(e.target.value)}
            className="text-[11px] px-2.5 h-7 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-secondary)] focus:outline-none focus:border-[var(--color-brand-primary)]"
            aria-label="Select file"
          >
            {files.map((f) => (
              <option key={f.fileId} value={f.fileId}>
                {f.fileName}
              </option>
            ))}
          </select>
        ) : null
      }
    >
      {!files ? (
        <Skeleton height="h-full" className="min-h-[220px]" />
      ) : !selected ? (
        <div className="py-10 text-center text-[12px] text-[var(--color-text-tertiary)]">
          No files match the current filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5 h-full min-h-0">
          <TierSummary file={selected} />
          <ColumnList file={selected} />
        </div>
      )}
    </ChartCard>
  );
}

function TierSummary({ file }: { file: FileBeforeAfter }) {
  const total = file.totalColumns;
  const rows: Array<{
    label: string;
    value: number;
    colour: string;
    tierBadge: "Green" | "Amber" | "Red" | "Unknown";
  }> = [
    {
      label: "Auto-mapped",
      value: file.autoMapped,
      colour: "var(--color-brand-primary)",
      tierBadge: "Green",
    },
    { label: "Light-touch review", value: file.lightReview, colour: "#EAB308", tierBadge: "Amber" },
    {
      label: "Manual intervention",
      value: file.manualMapped,
      colour: "var(--color-error)",
      tierBadge: "Red",
    },
    { label: "Unresolved", value: file.unresolved, colour: "#888780", tierBadge: "Unknown" },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)]">
          Total columns
        </div>
        <div
          className="text-[28px] font-semibold text-[var(--color-text-primary)] leading-tight"
          style={{ fontFamily: "var(--font-mono)" }}
        >
          {total}
        </div>
      </div>
      <div className="h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden flex">
        {rows.map((r) =>
          r.value === 0 ? null : (
            <div
              key={r.label}
              className="h-full"
              style={{ width: `${(r.value / total) * 100}%`, background: r.colour }}
              aria-label={`${r.label}: ${r.value}`}
            />
          )
        )}
      </div>
      <ul className="flex flex-col gap-1.5 mt-1">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-2 text-[12px]">
            <TierBadge tier={r.tierBadge} size="sm" />
            <span className="flex-1 text-[var(--color-text-primary)]">{r.label}</span>
            <span
              className="text-[var(--color-text-tertiary)]"
              style={{ fontFamily: "var(--font-mono)" }}
            >
              {r.value} ({pct(r.value, total)})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ColumnList({ file }: { file: FileBeforeAfter }) {
  return (
    <div className="border border-[var(--color-border-light)] rounded-lg overflow-hidden flex flex-col h-full min-h-0">
      <div className="grid grid-cols-[1fr_1fr_auto] gap-4 px-4 py-2 bg-[var(--color-bg-secondary)] text-[10px] uppercase tracking-wider text-[var(--color-text-tertiary)] font-semibold flex-shrink-0">
        <span>Source header</span>
        <span>Resolved target</span>
        <span>Tier</span>
      </div>
      <ul className="flex-1 min-h-0 overflow-y-auto">
        {file.columns.map((c, i) => (
          <li
            key={`${c.sourceColumn}-${i}`}
            className="grid grid-cols-[1fr_1fr_auto] gap-4 px-4 py-1.5 border-b border-[var(--color-border-light)] last:border-b-0 items-center text-[12px] hover:bg-[var(--color-bg-secondary)]"
          >
            <span
              className="truncate text-[var(--color-text-primary)]"
              style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}
              title={c.sourceColumn}
            >
              {c.sourceColumn}
            </span>
            <span
              className="truncate text-[var(--color-text-primary)]"
              title={c.targetField ?? "(unresolved)"}
            >
              {c.targetField ?? (
                <span className="italic text-[var(--color-text-tertiary)]">unresolved</span>
              )}
            </span>
            <TierBadge tier={tierForBadge(c.tier)} size="sm" />
          </li>
        ))}
      </ul>
    </div>
  );
}
